import React, { useState, useEffect } from 'react';
import { useSelector, connect } from 'react-redux';
import * as ViewSelectors from 'selectors/ViewSelectors';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { Theme, Checkbox, FormControlLabel, Box, FormHelperText } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import Container from 'components/Container';
import Typography from '@mui/material/Typography';
import { ReduxState } from 'services/types';
import {
	OrderProduct,
	UserDetailName,
	Shopper,
	ResponsiblePersonDetails,
	OrderInfo,
	ResponsiblePerson,
	Shop,
	PhoneObject,
	OrderObject,
	UnitSystemType,
	OnlineOrderRequest,
	UserProperty,
} from 'common/types';
import {
	updateShoppers,
	updateRentalInfo,
	resetNewRentalState,
	updatePhone,
	removeReservation,
} from 'actions';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { getRandomUserId } from 'services/utils';
import Button from '@mui/material/Button';
import { setNewOrder } from 'common/api/frontend';
import InputAdornment from '@mui/material/InputAdornment';
import countryCodes from 'common/services/countryCodes.json';
import {
	getVariantOrderProductsWithUnitsFromOrderProductList,
	getProductNameWithVariant,
} from 'common/utils/productUtils';
import {
	detailsIncludeMeasurementUnits,
	getSortedRequiredDetails,
	getUpdatedShopperFromDetailChange,
	getUpdatedShopperFromUnitSystemChange,
} from 'common/utils/userDetails';
import { replaceValues } from 'common/utils/common';
import { getTranslation } from 'common/modules/translations';
import Spinner from 'common/components/Spinner';
import * as IntercomService from 'services/intercom';
import { getResponsiblePersonDetails } from 'common/utils/newRentalUtils';

import UnitSystemSelect from 'common/components/UnitSystemSelect';
import UserDetailField from 'common/components/UserDetailField';
import SetPageNavAction from 'components/Portals/SetPageNavAction';
import SetPageTitle from 'components/Portals/SetPageTitle';
import { useTranslation } from 'services/localization/useTranslation';

import { Routes, push } from 'routing';
import { getOnlineOrderRequest } from 'common/utils/confirmation';
import { emailOrderConfirmation } from 'common/api/db/functions';
import { isValidEmail } from 'common/utils/validation';
import useShopFormat from 'hooks/useShopFormat';
import * as StockSelectors from 'selectors/StockSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import errorHandler from 'common/services/errorHandler';
import { getClientEnv } from 'common/utils/frontUtils';
import { getEmailTypeDetails } from 'views/Store/Tabs/Emails/utils';
import { CUSTOMER_FACING_EMAILS } from 'common/modules/emails/constants';

const emptyResponsiblePersonDetails: ResponsiblePersonDetails = {
	id: '',
	firstName: '',
	lastName: '',
	phone: '',
	email: '',
	marketing: false,
};

interface DispatchProps {
	updateShoppers: (shoppers: Shopper[]) => void;
	updateRentalInfo: (rentalInfo: OrderInfo) => void;
	resetNewRentalState: () => void;
	updatePhone: (phone: PhoneObject) => void;
	removeReservation: () => void;
}

interface StateToProps {
	responsiblePerson: ResponsiblePerson;
	rentalInfo: OrderInfo;
	shoppers: Shopper[];
	rentalProducts: OrderProduct[];
	shop: Shop;
	phone: PhoneObject;
}

interface FormErrors {
	firstName: string;
	lastName: string;
	phone: string;
	email: string;
}

type RentalType = 'rent-now' | 'reservation' | 'paylink';

type Props = DispatchProps &
	StateToProps &
	WithStyles<typeof styles> &
	RouteComponentProps<{ rentalType: RentalType }>;

const RentalShopperInfo = (props: Props) => {
	const { classes, shoppers } = props;
	const { t } = useTranslation();

	const language = useSelector(ViewSelectors.lang);
	const categories = useSelector(StockSelectors.categoriesData);
	const shopUnitSystem = useSelector(ShopSelectors.getShopUnitSystem);
	const locationEmailSettings = useSelector(ShopSelectors.activeLocationEmailSettings);
	const isAdminConfirmationEmailEnabled =
		locationEmailSettings?.emails?.adminOrderConfirmation?.enabled ?? true;

	const [responsiblePerson, setResponsiblePerson] = useState<ResponsiblePersonDetails>(
		getResponsiblePersonDetails(props.responsiblePerson, props.shoppers),
	);
	const [external, setExternal] = useState<boolean>(props.rentalInfo.responsiblePerson.external);
	const [formErrors, setFormErrors] = useState<FormErrors>({
		firstName: '',
		lastName: '',
		phone: '',
		email: '',
	});
	const [rentalType] = useState<string>(props.match.params.rentalType);
	const [loading, setLoading] = useState<boolean>(false);
	const [sendConfirmationEmail, setSendConfirmationEmail] = useState(
		isAdminConfirmationEmailEnabled,
	);
	const [unitSystem, setUnitSystem] = useState(shopUnitSystem);

	const { localFormat } = useShopFormat();

	useEffect(() => {
		return () => {
			props.removeReservation();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const updateResponsibleFirstName = (
		e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>,
	) => {
		const value = e.target.value;
		setResponsiblePerson({
			...responsiblePerson,
			firstName: value,
		});
		setFormErrors({
			...formErrors,
			firstName: '',
		});
	};

	const updateResponsibleLastName = (
		e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>,
	) => {
		const value = e.target.value;
		setResponsiblePerson({
			...responsiblePerson,
			lastName: value,
		});
		setFormErrors({
			...formErrors,
			lastName: '',
		});
	};

	const updateResponsiblePhoneNumber = (
		e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>,
	) => {
		const value = e.target.value;
		const phone = props.phone.areaCode + value;
		setResponsiblePerson({
			...responsiblePerson,
			phone,
		});
		setFormErrors({
			...formErrors,
			phone: '',
		});
		props.updatePhone({
			...props.phone,
			numberWithoutCode: value,
		});
	};

	const handleAreaCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const [countryCode, areaCode] = e.target.value.split(':');
		const phone = areaCode + props.phone.numberWithoutCode;
		setResponsiblePerson({
			...responsiblePerson,
			phone,
		});
		props.updatePhone({
			...props.phone,
			areaCode,
			countryCode,
		});
	};

	const updateResponsibleEmail = (
		e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>,
	) => {
		const value = e.target.value;
		setResponsiblePerson({
			...responsiblePerson,
			email: value,
		});
		setFormErrors({
			...formErrors,
			email: '',
		});
	};

	const updateResponsibleShopper = (shopperId: string | null) => {
		if (shopperId === null) {
			setResponsiblePerson({
				...emptyResponsiblePersonDetails,
				id: getRandomUserId(),
			});
			setExternal(true);
		} else {
			const shopper = props.shoppers.filter((s) => s.id === shopperId)[0];
			const { firstName, lastName, email, phone, marketing, id } = shopper;

			setResponsiblePerson({
				id,
				firstName,
				lastName,
				email,
				phone,
				marketing,
			});
			setExternal(false);
		}
		setFormErrors({
			firstName: '',
			lastName: '',
			email: '',
			phone: '',
		});
		props.updatePhone({
			...props.phone,
			numberWithoutCode: '',
		});
	};

	const getResponsibleShopper = (shoppers: Shopper[]): Shopper | null => {
		return shoppers.filter((s) => s.id === responsiblePerson.id)[0] || null;
	};

	const handleDetailChange = (
		value: UserProperty | null,
		detail: UserDetailName,
		shopperId: string,
	) => {
		const shoppers = props.shoppers.map((s) =>
			s.id === shopperId ? getUpdatedShopperFromDetailChange(s, detail, value) : s,
		);
		setFormErrors({
			...formErrors,
			[shopperId]: { ...formErrors[shopperId], [detail]: '' },
		});
		props.updateShoppers(shoppers);
	};

	const handleUnitSystemChange = (newUnitSystem: UnitSystemType) => {
		setUnitSystem(newUnitSystem);
		const updatedShoppers = props.shoppers.map((shopper) =>
			getUpdatedShopperFromUnitSystemChange(shopper, newUnitSystem),
		);
		props.updateShoppers(updatedShoppers);
	};

	const updateShopperFirstName = (firstName: string, shopperId: string) => {
		const shoppers = props.shoppers.map((shopper) => {
			if (shopper.id === shopperId) {
				return {
					...shopper,
					firstName,
				};
			}
			return shopper;
		});
		setFormErrors({
			...formErrors,
			[shopperId]: {
				...formErrors[shopperId],
				firstName: '',
			},
		});
		props.updateShoppers(shoppers);
	};

	const updateShopperLastName = (lastName: string, shopperId: string) => {
		const shoppers = props.shoppers.map((shopper) => {
			if (shopper.id === shopperId) {
				return {
					...shopper,
					lastName,
				};
			}
			return shopper;
		});
		props.updateShoppers(shoppers);
	};

	const handleMarketingConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		setResponsiblePerson({
			...responsiblePerson,
			marketing: checked,
		});
	};

	const renderShopperInfo = (shopper: Shopper, shopperIndex: number) => {
		const { classes, phone, rentalProducts } = props;
		const shopperProducts = rentalProducts.filter((p) => p.shopperId === shopper.id);
		const shopperProductsWithUnits = getVariantOrderProductsWithUnitsFromOrderProductList(
			shopperProducts,
		);
		const details = getSortedRequiredDetails(shopperProducts);
		const isResponsiblePerson = responsiblePerson.id === shopper.id;
		const shopperFormErrors = formErrors[shopper.id] || {};
		const responsibleFormErrors = formErrors;
		const selectedAreaCode = props.phone.countryCode + ':' + props.phone.areaCode;
		return (
			<Grid container key={shopper.id} spacing={2} className={classes.shopperContainer}>
				<Grid
					item
					xs={12}
					container
					justifyContent="space-between"
					alignItems="center"
					className={classes.shopperHeaderContainer}
				>
					<Grid item xs={6}>
						<Typography variant="h6">
							{t('common:person.person')} {shopperIndex + 1}
						</Typography>
					</Grid>
					<Grid item xs={6} style={{ textAlign: 'right' }}>
						<Button
							className={isResponsiblePerson ? classes.isResponsibleBtn : undefined}
							color="primary"
							variant={isResponsiblePerson ? 'text' : 'outlined'}
							onClick={() => updateResponsibleShopper(shopper.id)}
						>
							{isResponsiblePerson
								? t('details.responsiblePerson', 'Contact person')
								: t('details.setResponsiblePerson', 'Set as contact person')}
						</Button>
					</Grid>
				</Grid>
				<Grid item xs={12} sm={4} className={classes.shopperProductsContainer}>
					{shopperProductsWithUnits.map((p) => (
						<Typography key={p.id}>
							{p.units}x{' '}
							{getProductNameWithVariant(
								getTranslation(p.name, language),
								p.variant || null,
								language,
								undefined,
							)}
						</Typography>
					))}
				</Grid>
				<Grid item xs={12} sm={8} container spacing={2} className={classes.shopperDetailsContainer}>
					<Grid item xs={12}>
						<Typography variant="h6" color="secondary">
							{t('details.contactInformation', 'Contact information')}
						</Typography>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							id="firstName"
							label={t('common:form.firstName')}
							name="firstName"
							className={classes.textField}
							value={isResponsiblePerson ? responsiblePerson.firstName : shopper.firstName}
							onChange={
								isResponsiblePerson
									? updateResponsibleFirstName
									: (e) => updateShopperFirstName(e.target.value, shopper.id)
							}
							type="text"
							error={
								isResponsiblePerson
									? !!responsibleFormErrors.firstName
									: !!shopperFormErrors.firstName
							}
							helperText={
								isResponsiblePerson
									? responsibleFormErrors.firstName
									: shopperFormErrors.firstName || ''
							}
							variant="outlined"
							autoComplete="off"
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							id="lastName"
							label={t('common:form.lastName')}
							name="lastName"
							className={classes.textField}
							value={isResponsiblePerson ? responsiblePerson.lastName : shopper.lastName}
							onChange={
								isResponsiblePerson
									? updateResponsibleLastName
									: (e) => updateShopperLastName(e.target.value, shopper.id)
							}
							type="text"
							error={isResponsiblePerson && !!responsibleFormErrors.lastName}
							helperText={(isResponsiblePerson && responsibleFormErrors.lastName) || ''}
							variant="outlined"
							autoComplete="off"
						/>
					</Grid>
					{isResponsiblePerson && (
						<React.Fragment>
							<Grid item xs={12} sm={6}>
								<TextField
									id="phone"
									name="phone"
									label={t('common:form.phone')}
									className={classes.textField}
									variant="outlined"
									value={phone.numberWithoutCode}
									onChange={updateResponsiblePhoneNumber}
									type="tel"
									autoComplete="off"
									error={!!responsibleFormErrors.phone}
									helperText={responsibleFormErrors.phone}
									InputProps={{
										classes: { root: classes.noPaddingLeft },
										startAdornment: (
											<InputAdornment position="start" classes={{ root: classes.areaCodeRoot }}>
												<TextField
													select
													value={selectedAreaCode}
													onChange={handleAreaCodeChange}
													SelectProps={{
														native: true,
														classes: { select: classes.select },
													}}
													InputProps={{
														disableUnderline: true,
													}}
													variant="standard"
												>
													{countryCodes.map((countryCode) => (
														<option
															key={countryCode.code}
															value={countryCode.code + ':' + countryCode.dial_code}
														>
															{selectedAreaCode === countryCode.code + ':' + countryCode.dial_code
																? `${countryCode.flag} ${countryCode.dial_code}`
																: `${countryCode.flag} ${countryCode.name} (${countryCode.dial_code})`}
														</option>
													))}
												</TextField>
											</InputAdornment>
										),
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField
									id="email"
									label={t('common:form.email')}
									name="email"
									className={classes.textField}
									value={responsiblePerson.email || ''}
									onChange={updateResponsibleEmail}
									type="email"
									error={!!responsibleFormErrors.email}
									helperText={responsibleFormErrors.email || ''}
									variant="outlined"
									autoComplete="off"
								/>
							</Grid>
							<Grid item xs={12} sm={12}>
								<Box mt={1} />
								<FormControlLabel
									control={
										<Checkbox
											color="primary"
											checked={sendConfirmationEmail}
											disabled={!isAdminConfirmationEmailEnabled}
											onChange={(e) => setSendConfirmationEmail(e.target.checked)}
											value="sendConfirmationEmail"
										/>
									}
									label={
										<>
											<Typography variant="body1">
												{t('sendBookingConfirmationViaEmail2', 'Send order confirmation via email')}
											</Typography>
											<FormHelperText component="div">
												{isAdminConfirmationEmailEnabled
													? t('common:xIsMandatoryWhenThisSelected', {
															field: t('common:form.email'),
															defaultValue: '{{field}} is a mandatory field when this is selected',
													  })
													: t('emails.emailDisabledDisclaimer', {
															emailName: getEmailTypeDetails(
																t,
																CUSTOMER_FACING_EMAILS.adminOrderConfirmation,
															).heading,
															defaultValue: 'The {{emailName}} email is disabled in Email settings',
													  })}
											</FormHelperText>
										</>
									}
								/>
							</Grid>
							<Grid item xs={12} sm={12} className={classes.marketingCheckbox}>
								<FormControlLabel
									control={
										<Checkbox
											color="primary"
											checked={responsiblePerson.marketing}
											onChange={handleMarketingConsentChange}
											value="marketing"
										/>
									}
									label={
										<Typography variant="body1">
											{t(
												'details.marketingConsent',
												'Add to marketing list (consent given verbally)',
											)}
										</Typography>
									}
								/>
							</Grid>
						</React.Fragment>
					)}
					{!!details.length && (
						<Grid
							item
							container
							xs={12}
							className={classes.customerInfo}
							alignItems="baseline"
							justifyContent="space-between"
						>
							<Grid item xs={12} sm="auto">
								<Typography variant="h6" color="secondary">
									{t('details.customerInformation', 'Customer information')}
								</Typography>
							</Grid>
							{detailsIncludeMeasurementUnits(details) && (
								<Grid item xs={12} sm="auto">
									<UnitSystemSelect value={unitSystem} onChange={handleUnitSystemChange} t={t} />
								</Grid>
							)}
						</Grid>
					)}
					{details.map((detail, index) => (
						<Grid item xs={12} sm={6} key={detail + index}>
							<UserDetailField
								detail={detail}
								unitSystem={unitSystem}
								userProperty={shopper.userProperties?.[detail]}
								onChange={(newValue) => handleDetailChange(newValue, detail, shopper.id)}
								helperText={shopperFormErrors[detail] ?? ''}
								error={!!shopperFormErrors[detail]}
								t={t}
							/>
						</Grid>
					))}
				</Grid>
			</Grid>
		);
	};

	const renderExternalResponsiblePerson = () => {
		const { classes, phone } = props;
		const selectedAreaCode = props.phone.countryCode + ':' + props.phone.areaCode;
		if (!external) {
			return (
				<div className={classes.marginTop}>
					<Button onClick={() => updateResponsibleShopper(null)} variant="text" color="primary">
						{t('details.addResponsiblePerson', 'Add contact person')}
					</Button>
				</div>
			);
		}
		return (
			<div className={classes.marginTop}>
				<Grid container spacing={2} className={classes.shopperContainer}>
					<Grid
						item
						xs={12}
						container
						justifyContent="space-between"
						alignItems="center"
						className={classes.shopperHeaderContainer}
					>
						<Grid item xs={6}>
							<Typography variant="h6">
								{t('details.separateResponsiblePerson', 'Separate contact person')}
							</Typography>
						</Grid>
						<Grid item xs={6} style={{ textAlign: 'right' }}>
							<Button className={classes.isResponsibleBtn} color="inherit" variant="text">
								{t('details.responsiblePerson', 'Contact person')}
							</Button>
						</Grid>
					</Grid>
					<Grid item xs={12} sm={4} className={classes.shopperProductsContainer}>
						<div />
					</Grid>
					<Grid
						item
						xs={12}
						sm={8}
						container
						spacing={2}
						className={classes.shopperDetailsContainer}
					>
						<Grid item xs={12} sm={6}>
							<TextField
								id="firstName"
								label={t('common:form.firstName')}
								name="firstName"
								className={classes.textField}
								value={responsiblePerson.firstName}
								onChange={updateResponsibleFirstName}
								type="text"
								error={!!formErrors.firstName}
								helperText={formErrors.firstName}
								variant="outlined"
								autoComplete="off"
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								id="lastName"
								label={t('common:form.lastName')}
								name="lastName"
								className={classes.textField}
								value={responsiblePerson.lastName}
								onChange={updateResponsibleLastName}
								type="text"
								error={!!formErrors.lastName}
								helperText={formErrors.lastName}
								variant="outlined"
								autoComplete="off"
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								id="phone"
								name="phone"
								label={t('common:form.phone')}
								className={classes.textField}
								variant="outlined"
								value={phone.numberWithoutCode}
								onChange={updateResponsiblePhoneNumber}
								type="tel"
								autoComplete="off"
								error={!!formErrors.phone}
								helperText={formErrors.phone}
								InputProps={{
									classes: { root: classes.noPaddingLeft },
									startAdornment: (
										<InputAdornment position="start" classes={{ root: classes.areaCodeRoot }}>
											<TextField
												select
												value={selectedAreaCode}
												onChange={handleAreaCodeChange}
												SelectProps={{
													native: true,
													classes: { select: classes.select },
												}}
												InputProps={{
													disableUnderline: true,
												}}
												variant="standard"
											>
												{countryCodes.map((countryCode) => (
													<option
														key={countryCode.code}
														value={countryCode.code + ':' + countryCode.dial_code}
													>
														{selectedAreaCode === countryCode.code + ':' + countryCode.dial_code
															? `${countryCode.flag} ${countryCode.dial_code}`
															: `${countryCode.flag} ${countryCode.name} (${countryCode.dial_code})`}
													</option>
												))}
											</TextField>
										</InputAdornment>
									),
								}}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								id="email"
								label={t('common:form.email')}
								name="email"
								className={classes.textField}
								value={responsiblePerson.email || ''}
								onChange={updateResponsibleEmail}
								type="email"
								error={!!formErrors.email}
								helperText={formErrors.email}
								variant="outlined"
								autoComplete="off"
							/>
						</Grid>
						<Grid item xs={12} sm={12}>
							<Box mt={1} />
							<FormControlLabel
								control={
									<Checkbox
										color="primary"
										checked={sendConfirmationEmail}
										disabled={!isAdminConfirmationEmailEnabled}
										onChange={(e) => setSendConfirmationEmail(e.target.checked)}
										value="sendConfirmationEmail"
									/>
								}
								label={
									<>
										<Typography variant="body1">
											{t('sendBookingConfirmationViaEmail2', 'Send order confirmation via email')}
										</Typography>
										<FormHelperText component="div">
											{isAdminConfirmationEmailEnabled
												? t('common:xIsMandatoryWhenThisSelected', {
														field: t('common:form.email'),
														defaultValue: '{{field}} is a mandatory field when this is selected',
												  })
												: t('emails.emailDisabledDisclaimer', {
														emailName: getEmailTypeDetails(
															t,
															CUSTOMER_FACING_EMAILS.adminOrderConfirmation,
														).heading,
														defaultValue: 'The {{emailName}} email is disabled in Email settings',
												  })}
										</FormHelperText>
									</>
								}
							/>
						</Grid>
						<Grid item xs={12} sm={12} className={classes.marketingCheckbox}>
							<FormControlLabel
								control={
									<Checkbox
										color="primary"
										checked={responsiblePerson.marketing}
										onChange={handleMarketingConsentChange}
										value="marketing"
									/>
								}
								label={
									<Typography variant="body1">
										{t(
											'details.marketingConsent',
											'Add to marketing list (consent given verbally)',
										)}
									</Typography>
								}
							/>
						</Grid>
					</Grid>
				</Grid>
			</div>
		);
	};

	const submit = async () => {
		const rentalInfo = props.rentalInfo;
		let shoppers = props.shoppers;
		const requiredField = t('common:mandatory');
		let formValid = true;
		const formErrors = {
			firstName: '',
			lastName: '',
			phone: '',
			email: '',
		};
		if (!responsiblePerson.firstName) {
			formErrors.firstName = requiredField;
			formValid = false;
		}
		if (!responsiblePerson.lastName) {
			formErrors.lastName = requiredField;
			formValid = false;
		}
		if (sendConfirmationEmail && !responsiblePerson.email) {
			formErrors.email = requiredField;
			formValid = false;
		} else if (sendConfirmationEmail && !isValidEmail(responsiblePerson.email)) {
			formErrors.email = t('common:validation.isNotValidEmail');
			formValid = false;
		}
		if (rentalType === 'rent-now') {
			for (const shopper of shoppers) {
				if (!formErrors[shopper.id]) {
					formErrors[shopper.id] = {};
				}
				if (!shopper.firstName && shopper.id !== responsiblePerson.id) {
					formErrors[shopper.id].firstName = requiredField;
					formValid = false;
				}
				const shopperProducts = props.rentalProducts.filter((p) => p.shopperId === shopper.id);
				const details = getSortedRequiredDetails(shopperProducts);
				for (const detail of details) {
					if (!shopper.userProperties[detail]) {
						formErrors[shopper.id][detail] = requiredField;
						formValid = false;
					}
				}
			}
		}
		setFormErrors(formErrors);
		if (formValid) {
			setLoading(true);
			let newResponsiblePerson = {} as ResponsiblePerson;
			if (external) {
				newResponsiblePerson = {
					external: true,
					person: responsiblePerson,
				};
			} else {
				const responsibleShopper = getResponsibleShopper(shoppers)!;
				shoppers = shoppers.map((shopper) => {
					if (responsibleShopper.id === shopper.id) {
						return {
							...shopper,
							...responsiblePerson,
							userProperties: shopper.userProperties,
						};
					}
					return shopper;
				});
				newResponsiblePerson = {
					external: false,
					shopperId: responsiblePerson.id,
				};
			}
			const newRentalInfo: OrderInfo = {
				...rentalInfo,
				responsiblePerson: newResponsiblePerson,
			};
			props.updateRentalInfo(newRentalInfo);
			props.updateShoppers(shoppers);
			const rental: OrderObject = {
				products: props.rentalProducts,
				rentalInfo: newRentalInfo,
				shoppers,
			};
			await createRental(rental);
			props.resetNewRentalState();
			setLoading(false);
			IntercomService.trackEvent(IntercomService.CREATED_ORDER, { channel: 'ADMIN' });
			if (rentalType === 'rent-now') {
				push(Routes.BookingPage(newRentalInfo.id));
			}
			if (rentalType === 'reservation') {
				push(Routes.NewRentalDone('reservation'), {
					bookingId: newRentalInfo.id,
				});
			}
		}
	};

	const createRental = async (rental: OrderObject) => {
		const updatedRentalInfo: OrderInfo = {
			...rental.rentalInfo,
			activeState: 'ADMIN_BOOKED',
			rentalState: 'BOOKED',
			shopId: props.shop.shopInfo.id,
		};
		const updatedRental: OrderObject = {
			...rental,
			rentalInfo: updatedRentalInfo,
		};
		try {
			await setNewOrder(updatedRental);
			props.removeReservation();
			// send contract email
			if (sendConfirmationEmail) {
				await sendEmail(updatedRental);
			}
		} catch (e) {
			setLoading(false);
			errorHandler.report(e);
		}
	};

	const sendEmail = async (order: OrderObject) => {
		if (!props.shop.shopUrls) {
			throw new Error(`No shop url found for shop ${props.shop.shopInfo.id}`);
		}
		const orderData = getOnlineOrderRequest({
			order,
			shopInfo: props.shop.publicInfo,
			categories,
			lang: language,
			shopUrlDoc: props.shop.shopUrls,
			isMissingDetails: false,
			locationId: order.rentalInfo.startLocation.id,
			localFormat,
			t,
			env: getClientEnv(),
		});
		const orderDataNotNaN = replaceValues(orderData, Number.isNaN, 0) as OnlineOrderRequest;
		await emailOrderConfirmation({ data: orderDataNotNaN, email: undefined });
	};

	if (!props.rentalInfo.id || !props.rentalProducts.length) {
		return <Redirect to={Routes.NEW_RENTAL} />;
	}
	return (
		<>
			<SetPageNavAction
				priority={2}
				backAction={{
					title: t('newRental.editRental', 'Edit rental'),
					to: Routes.NEW_RENTAL,
					onClick: () => props.removeReservation(),
				}}
			/>
			<SetPageTitle
				priority={2}
				title={t('newRental.customerDetails', 'Customer details')}
				rightContent={
					<Button
						className={classes.continueBtn}
						variant="contained"
						color="primary"
						onClick={submit}
						disabled={loading}
						data-qa="confirmBooking"
					>
						{loading ? (
							<Spinner padding={4} />
						) : rentalType === 'rent-now' ? (
							t('shopperInfo.confirmBookingBtn2', 'Confirm order')
						) : (
							t('shopperInfo.saveBookingBtn2', 'Save order')
						)}
					</Button>
				}
			/>
			<Container>
				{rentalType === 'reservation' && (
					<Typography variant="body1" gutterBottom className={classes.typeHelperText}>
						{t(
							'shopperInfo.fillAtLeastNameNumberEmail',
							`Fill at least the contact person's name, phone number and email.`,
						)}
					</Typography>
				)}
				<div>
					{shoppers.map((shopper, index) => renderShopperInfo(shopper, index))}
					{renderExternalResponsiblePerson()}
				</div>
			</Container>
		</>
	);
};

const mapStateToProps = ({ newRental, shop }: ReduxState): StateToProps => {
	return {
		responsiblePerson: newRental.rentalInfo.responsiblePerson,
		rentalInfo: newRental.rentalInfo,
		shoppers: newRental.shoppers,
		rentalProducts: newRental.products,
		shop: shop.shop.data!,
		phone: newRental.phone,
	};
};

const styles = (theme: Theme) =>
	createStyles({
		customerInfo: {
			marginTop: theme.spacing(1),
		},
		textField: {
			width: '100%',
		},
		menu: {
			width: 200,
		},
		isResponsibleBtn: {
			backgroundColor: 'transparent',
			color: theme.palette.primary.light,
			cursor: 'default',
			pointerEvents: 'none',
		},
		shopperContainer: {
			backgroundColor: theme.palette.background.paper,
			padding: 16,
			margin: '16px 0',
		},
		shopperHeaderContainer: {},
		shopperProductsContainer: {},
		shopperDetailsContainer: {},
		marginTop: {
			marginTop: 32,
		},
		typeHelperText: {
			marginTop: 16,
			textAlign: 'center',
		},
		areaCodeRoot: {
			borderRight: '1px solid rgba(0, 0, 0, 0.23)',
			padding: '28px 0',
			paddingLeft: 14,
			flex: '0 0 110px',
		},
		noPaddingLeft: {
			paddingLeft: 0,
		},
		marketingCheckbox: {
			marginTop: theme.spacing(-1),
		},
		select: {
			width: 'calc(100% - 24px)',
			paddingRight: 24,
		},
		continueBtn: {
			minHeight: 40,
		},
	});

const dispatchProps: DispatchProps = {
	updateShoppers,
	updateRentalInfo,
	resetNewRentalState,
	updatePhone,
	removeReservation,
};

export default withStyles(styles)(connect(mapStateToProps, dispatchProps)(RentalShopperInfo));
